import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { addObservation, fetchObservations, fetchUsersForDelegation, resolveObservation } from '../../services/api';
import styles from './ProductObservations.module.css';

const ProductObservations = ({ productCode, origem }) => {
    const [observation, setObservation] = useState('');
    const [delegatedTo, setDelegatedTo] = useState('');
    const [resolvingId, setResolvingId] = useState(null);
    const [resolutionMessage, setResolutionMessage] = useState('');
    const queryClient = useQueryClient();

    // Buscar observações
    const { data: observationsData, isLoading: isLoadingObs } = useQuery({
        queryKey: ['observations', productCode],
        queryFn: () => fetchObservations(productCode, origem)
    });

    // Buscar usuários para delegação
    const { data: usersData } = useQuery({
        queryKey: ['users-delegation'],
        queryFn: fetchUsersForDelegation
    });

    // Mutation para adicionar observação
    const addMutation = useMutation({
        mutationFn: (newObs) => addObservation(newObs),
        onSuccess: () => {
            queryClient.invalidateQueries(['observations', productCode]);
            setObservation('');
            setDelegatedTo('');
        },
        onError: (error) => {
            alert('Erro ao adicionar observação: ' + error.message);
        }
    });

    // Mutation para resolver observação
    // Mutation para resolver observação
    const resolveMutation = useMutation({
        mutationFn: (data) => resolveObservation(data),
        onSuccess: () => {
            queryClient.invalidateQueries(['observations', productCode]);
            setResolvingId(null);
            setResolutionMessage('');
        },
        onError: (error) => {
            alert('Erro ao resolver observação: ' + error.message);
        }
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!observation.trim()) return;

        addMutation.mutate({
            productCode,
            origem_produto: origem,
            observation,
            delegatedToUserId: delegatedTo || null
        });
    };

    const handleResolveClick = (id) => {
        setResolvingId(id);
        setResolutionMessage('');
    };

    const confirmResolve = (id) => {
        resolveMutation.mutate({ id, message: resolutionMessage });
    };

    const cancelResolve = () => {
        setResolvingId(null);
        setResolutionMessage('');
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('pt-BR');
    };

    const observations = observationsData?.data || [];
    const users = usersData?.data || [];

    return (
        <div className={styles.container}>
            <h2 className={styles.title}>📝 Observações e Delegações</h2>

            <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.formGroup}>
                    <label className={styles.label}>Nova Observação</label>
                    <textarea
                        className={styles.textarea}
                        value={observation}
                        onChange={(e) => setObservation(e.target.value)}
                        placeholder="Digite sua observação aqui..."
                        required
                    />
                </div>

                <div className={styles.formGroup}>
                    <label className={styles.label}>Delegar para (opcional)</label>
                    <select
                        className={styles.select}
                        value={delegatedTo}
                        onChange={(e) => setDelegatedTo(e.target.value)}
                    >
                        <option value="">-- Selecione um usuário --</option>
                        {users.map((user) => (
                            <option key={user.id} value={user.id}>
                                {user.nome}
                            </option>
                        ))}
                    </select>
                </div>

                <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={addMutation.isPending}
                >
                    {addMutation.isPending ? 'Salvando...' : 'Adicionar Observação'}
                </button>
            </form>

            <div className={styles.list}>
                {isLoadingObs ? (
                    <p>Carregando observações...</p>
                ) : observations.length === 0 ? (
                    <p className={styles.emptyMessage}>Nenhuma observação registrada para este produto.</p>
                ) : (
                    observations.map((obs) => (
                        <div key={obs.id} className={styles.observationCard}>
                            <div className={styles.cardHeader}>
                                <div className={styles.userInfo}>
                                    <span className={styles.userName}>{obs.created_by_user_name || 'Usuário'}</span>
                                    <span className={styles.date}>{formatDate(obs.created_at)}</span>
                                    {obs.delegated_to_user_name && (
                                        <span className={styles.delegationInfo}>
                                            Delegado para: <strong>{obs.delegated_to_user_name}</strong>
                                        </span>
                                    )}
                                </div>
                                <div>
                                    {obs.resolved ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                            <span className={styles.resolvedBadge}>Resolvido</span>
                                            {obs.resolution_note && (
                                                <small style={{ color: '#6c757d', fontSize: '0.8em', marginTop: '4px', textAlign: 'right' }}>
                                                    Resp: {obs.resolution_note}
                                                </small>
                                            )}
                                        </div>
                                    ) : resolvingId === obs.id ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '250px' }}>
                                            <textarea
                                                className="form-control"
                                                placeholder="Mensagem de resolução (opcional)..."
                                                value={resolutionMessage}
                                                onChange={(e) => setResolutionMessage(e.target.value)}
                                                rows="2"
                                                style={{ fontSize: '0.9em' }}
                                            />
                                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                <button
                                                    onClick={cancelResolve}
                                                    className="btn btn-sm btn-light border"
                                                    style={{ fontSize: '0.8em' }}
                                                >
                                                    Cancelar
                                                </button>
                                                <button
                                                    onClick={() => confirmResolve(obs.id)}
                                                    className="btn btn-sm btn-success"
                                                    style={{ fontSize: '0.8em' }}
                                                    disabled={resolveMutation.isPending}
                                                >
                                                    {resolveMutation.isPending ? '...' : 'Concluir'}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => handleResolveClick(obs.id)}
                                            className="btn btn-sm btn-outline-success"
                                            style={{ fontSize: '0.8em', minWidth: '80px' }}
                                        >
                                            Responder e Resolver
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className={styles.content}>{obs.observation}</div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default ProductObservations;
